import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import {
  CourseRequestClient,
  CourseRequestCreateDTO,
  CourseRequestDTO,
  CourseRequestSearchRequestDTO,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';

const apiClient = new CourseRequestClient(API_BASE_URL, axiosInstance);

// A member asking, from Explore, for a course that is not on GolfPlayed. Always filed against the
// member behind the token; there is no member id to pass.
export const useRequestCourse = (): UseMutationResult<
  CourseRequestDTO | undefined,
  Error,
  CourseRequestCreateDTO
> =>
  useMutation({
    mutationFn: async (data: CourseRequestCreateDTO) => {
      const response = await apiClient.requestCourse(data);
      return response?.data;
    },
  });

interface CourseRequestSearchResult {
  results: CourseRequestDTO[];
  totalCount: number;
}

// Direct API search (no caching), for the admin table with server-side paging.
export const useCourseRequestSearch = (): {
  search: (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: CourseRequestSearchRequestDTO;
  }) => Promise<CourseRequestSearchResult>;
} => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: CourseRequestSearchRequestDTO;
  }): Promise<CourseRequestSearchResult> => {
    const response = await apiClient.searchCourseRequests({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      // Oldest first: it is a queue, and the longest wait is the next one to deal with.
      sortDescending: params.sortDescending ?? false,
      filters: params.filters || {},
    });
    return {
      // NSwag's ApiResponse_searchResponseDTO_1 codegen collision (see useStateApi.ts). The
      // runtime data is genuinely CourseRequestDTO[].
      results:
        (response?.data?.results as CourseRequestDTO[] | undefined) || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

export const useCourseRequestById = (
  id: string | null | undefined,
): UseQueryResult<CourseRequestDTO | undefined> =>
  useQuery({
    queryKey: ['courseRequests', id],
    queryFn: async () => {
      const response = await apiClient.getCourseRequestById(id as string);
      return response?.data;
    },
    enabled: !!id,
  });

export const useApproveCourseRequest = (): UseMutationResult<
  CourseRequestDTO | undefined,
  Error,
  { id: string; courseId: string; reviewNotes?: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      courseId,
      reviewNotes,
    }: {
      id: string;
      courseId: string;
      reviewNotes?: string;
    }) => {
      const response = await apiClient.approveCourseRequest(id, {
        courseId,
        reviewNotes,
      });
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courseRequests'] });
    },
  });
};

export const useDeclineCourseRequest = (): UseMutationResult<
  CourseRequestDTO | undefined,
  Error,
  { id: string; reviewNotes?: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      reviewNotes,
    }: {
      id: string;
      reviewNotes?: string;
    }) => {
      const response = await apiClient.declineCourseRequest(id, {
        reviewNotes,
      });
      return response?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courseRequests'] });
    },
  });
};
